// Lib
import React, { useCallback, useRef, useState } from 'react';

// Components
import { ImageModalV2EditorContent, type ImageModalV2EditorContentProps } from './ImageModalV2EditorContent';
import ImageModalV2EditorToolbar from '../toolbar/ImageModalV2EditorToolbar';

// Hooks
import { useImageModalEditorVisualCommit } from '../hooks/useImageModalEditorVisualCommit';
import {
    IMAGE_MODAL_V2_OPERATION_MESSAGE,
    type ImageModalV2OperationToastState,
} from '../toolbar/imageModalV2OperationToastTypes';
import { INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS } from '../../backgroundRemoval/imageModalBackgroundRemovalProgressTypes';

type ImageModalV2EditorProps = Omit<
    ImageModalV2EditorContentProps,
    | 'editorIsReady'
    | 'isAwaitingPreviewPaint'
    | 'editorContentRef'
    | 'showImageReplacementWipe'
    | 'showEditorLoadingPreview'
    | 'toolbarClassName'
    | 'onEditorReadyChange'
    | 'renderToolbar'
    | 'onSaveFreezeStart'
> & {
    beginSaveHandoff: () => void;
    onEditorReadyChange?: (ready: boolean) => void;
    onOperationToastChange: (toast: ImageModalV2OperationToastState | null) => void;
};

/** V2 Cropper shell: reveals the editor only after its painted surface is measurable. */
const ImageModalV2Editor: React.FC<ImageModalV2EditorProps> = ({
    onEditorReadyChange,
    onOperationToastChange,
    beginSaveHandoff,
    ...props
}) => {
    const [editorIsReady, setEditorIsReady] = useState(false);
    const [hasRevealedEditor, setHasRevealedEditor] = useState(false);
    const editorContentRef = useRef<HTMLDivElement>(null);
    const isEditorSurfaceReady = useCallback(() => {
        const editorContent = editorContentRef.current;
        const cropperImage = editorContent?.querySelector<HTMLImageElement>('.cropper-canvas img');
        if (!editorContent || !cropperImage) return false;

        const bounds = cropperImage.getBoundingClientRect();
        const cropperImageStyle = getComputedStyle(cropperImage);
        return (
            Number(getComputedStyle(editorContent).opacity) > 0 &&
            Number(cropperImageStyle.opacity) > 0 &&
            cropperImageStyle.visibility !== 'hidden' &&
            bounds.width > 0 &&
            bounds.height > 0
        );
    }, []);
    const handleVisualReadyChange = useImageModalEditorVisualCommit(isEditorSurfaceReady, onEditorReadyChange);

    const handleEditorReadyChange = useCallback(
        (ready: boolean) => {
            setEditorIsReady(ready);

            if (!ready) {
                handleVisualReadyChange(false);
                return;
            }

            setHasRevealedEditor(true);
            handleVisualReadyChange(true);
        },
        [handleVisualReadyChange],
    );

    return (
        <ImageModalV2EditorContent
            {...props}
            editorIsReady={editorIsReady}
            isAwaitingPreviewPaint={!hasRevealedEditor}
            editorContentRef={editorContentRef}
            showImageReplacementWipe={false}
            showEditorLoadingPreview={!editorIsReady && !hasRevealedEditor}
            toolbarClassName="ImageEditorModalToolbar ImageEditorModalToolbar-v2"
            onEditorReadyChange={handleEditorReadyChange}
            renderToolbar={(toolbarProps) => <ImageModalV2EditorToolbar {...toolbarProps} />}
            onSaveFreezeStart={() => {
                beginSaveHandoff();
                onOperationToastChange({
                    message: IMAGE_MODAL_V2_OPERATION_MESSAGE.SAVING_CHANGES,
                    progress: INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
                });
            }}
        />
    );
};

export default ImageModalV2Editor;
